import type { SVGProps } from "react";
import { cn } from "./cn";

export type IconSize = "xs" | "sm" | "md" | "lg" | "xl";

export interface IconProps extends SVGProps<SVGSVGElement> {
  /** Taille prédéfinie de l'icône ou nombre en pixels */
  size?: IconSize | number;
  /** Épaisseur du tracé SVG (défaut: 1.75) */
  strokeWidth?: number | string;
  /** Titre optionnel pour l'accessibilité (si omis, l'icône est considérée comme décorative) */
  title?: string;
}

const SIZE_MAP: Record<IconSize, string> = {
  xs: "size-3", // 12px
  sm: "size-4", // 16px
  md: "size-5", // 20px
  lg: "size-6", // 24px
  xl: "size-8", // 32px
};

/**
 * Composant de base SVG pour le jeu d'icônes Nexty.
 *
 * Grille 24x24, épaisseur de trait 1.75 par défaut, couleur héritée du texte (`currentColor`).
 * Conçu à 100% sur mesure pour garantir la propriété intellectuelle et zéro conflit de licence.
 */
export function Icon({
  size = "md",
  strokeWidth = 1.75,
  title,
  className,
  style,
  children,
  ...props
}: IconProps) {
  const sizeClass = typeof size === "string" ? SIZE_MAP[size] : undefined;
  const customStyle =
    typeof size === "number"
      ? { width: `${size}px`, height: `${size}px`, ...style }
      : style;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      className={cn("inline-block shrink-0 align-middle transition-colors duration-150", sizeClass, className)}
      style={customStyle}
      {...props}
    >
      {title && <title>{title}</title>}
      {children}
    </svg>
  );
}
